/**
 * One choice of a few, as a card. Replaces `ChoiceCard`, which stays in
 * `Card.tsx` until its call sites move.
 *
 * The measured card: 178 tall (more if the words need it), 16 of padding, r12,
 * a 32 grey glyph tile, the title 20 below it at 14/20 600, the description 2
 * below that. A 16px indicator sits at the top right. Selected, the card takes
 * the primary-50 fill, a primary-600 edge, a primary tile and primary words.
 * A floating badge sits centred on the top edge.
 *
 * `RadioCardGroup` is the radiogroup. One card holds the tab stop and the
 * arrow keys move the choice, which is how a native radio group behaves and
 * what a keyboard user expects of anything that calls itself one.
 */
import type { KeyboardEvent, ReactNode } from 'react'
import { createContext, useContext, useRef } from 'react'
import { cn } from '../lib/cn'
import { Badge } from './Badge'
import { FeaturedIcon } from './FeaturedIcon'
import { IconCheck } from './icons'

interface GroupState {
  value: string | null
  onChange: (value: string) => void
  /** The value that holds the tab stop. */
  focusValue: string | null
}

const GroupContext = createContext<GroupState | null>(null)

/** The arrow keys that move a radio group, and which way. */
const STEP: Record<string, 1 | -1> = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }

/**
 * Where the choice moves on an arrow key, skipping disabled cards and wrapping
 * at both ends. Null when the key is not an arrow or nothing can be chosen.
 */
export function nextRadioValue(
  options: readonly { value: string; disabled?: boolean }[],
  current: string | null,
  key: string,
): string | null {
  const step = STEP[key]
  if (step === undefined) return null
  const usable = options.filter((option) => option.disabled !== true).map((option) => option.value)
  if (usable.length === 0) return null
  const at = current === null ? -1 : usable.indexOf(current)
  if (at === -1) return step === 1 ? (usable[0] as string) : (usable[usable.length - 1] as string)
  return usable[(at + step + usable.length) % usable.length] as string
}

/** Which card takes the tab stop: the chosen one, or the first that can be chosen. */
export function tabStopValue(options: readonly { value: string; disabled?: boolean }[], value: string | null): string | null {
  const chosen = options.find((option) => option.value === value && option.disabled !== true)
  if (chosen) return chosen.value
  return options.find((option) => option.disabled !== true)?.value ?? null
}

export interface RadioCardGroupProps {
  value: string | null
  onChange: (value: string) => void
  /** Names the group, since the cards inside only name themselves. */
  label: string
  /**
   * The cards' values in order, with which are disabled, so the arrow keys
   * know where they can go. Read from the cards when it is left out.
   */
  options?: readonly { value: string; disabled?: boolean }[]
  children: ReactNode
  className?: string
  /**
   * Cards across from 640px, three by default. A prop rather than a class,
   * because `cn` only joins: a caller's `lg:grid-cols-2` sorts after the
   * group's own `sm:grid-cols-3` and wins only from 1024px, which leaves three
   * columns between the two widths.
   */
  columns?: RadioCardColumns
}

export type RadioCardColumns = 1 | 2 | 3 | 4

/** Written out whole so Tailwind sees every class. Four steps down to two below 1024px. */
const GROUP_COLUMNS: Record<RadioCardColumns, string> = {
  1: 'grid-cols-1',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
  4: 'sm:grid-cols-2 lg:grid-cols-4',
}

export function RadioCardGroup({ value, onChange, label, options, children, className, columns = 3 }: RadioCardGroupProps) {
  const ref = useRef<HTMLDivElement>(null)

  const readOptions = (): { value: string; disabled?: boolean }[] => {
    if (options) return [...options]
    const nodes = ref.current?.querySelectorAll<HTMLElement>('[data-radio-value]') ?? []
    return Array.from(nodes).map((node) => ({
      value: node.dataset.radioValue ?? '',
      disabled: node.getAttribute('aria-disabled') === 'true',
    }))
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const next = nextRadioValue(readOptions(), value, event.key)
    if (next === null) return
    event.preventDefault()
    onChange(next)
    ref.current?.querySelector<HTMLElement>(`[data-radio-value="${CSS.escape(next)}"]`)?.focus()
  }

  const focusValue = options ? tabStopValue(options, value) : value

  return (
    <GroupContext.Provider value={{ value, onChange, focusValue }}>
      <div
        ref={ref}
        role="radiogroup"
        aria-label={label}
        onKeyDown={onKeyDown}
        className={cn('grid gap-4', GROUP_COLUMNS[columns], className)}
      >
        {children}
      </div>
    </GroupContext.Provider>
  )
}

export interface RadioCardProps {
  icon: ReactNode
  title: ReactNode
  description: ReactNode
  value: string
  /** A badge floating on the top edge, such as `Recommended`. */
  badge?: ReactNode
  /**
   * The default the design recommends. Draws a `Recommended` badge when no
   * other is given, and a tinted edge while it is not the one chosen.
   */
  recommended?: boolean
  disabled?: boolean
  /** Why it cannot be chosen. Drawn as a line under the description. */
  disabledReason?: ReactNode
  /** For a card outside a group. Inside one, the group decides. */
  selected?: boolean
  onSelect?: () => void
  className?: string
}

export function RadioCard({
  icon,
  title,
  description,
  value,
  badge,
  recommended = false,
  disabled = false,
  disabledReason,
  selected,
  onSelect,
  className,
}: RadioCardProps) {
  const group = useContext(GroupContext)
  const isSelected = group ? group.value === value : selected === true
  // With nothing chosen and no `options` to say which card comes first, every
  // usable card keeps a stop, so the group can never be unreachable by Tab.
  const holdsTabStop = group ? (group.focusValue === null ? !disabled : group.focusValue === value) : true
  const floating = badge ?? (recommended ? <Badge tone="accent">Recommended</Badge> : null)

  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      aria-disabled={disabled ? true : undefined}
      data-radio-value={value}
      data-recommended={recommended ? '' : undefined}
      tabIndex={holdsTabStop ? 0 : -1}
      onClick={() => {
        if (disabled) return
        if (group) group.onChange(value)
        onSelect?.()
      }}
      className={cn(
        'relative flex min-h-[11.125rem] flex-col items-start rounded-xl border p-4 text-left transition',
        disabled
          ? 'cursor-not-allowed border-line bg-surface-muted'
          : isSelected
            ? 'border-primary-600 bg-primary-50'
            : cn(recommended ? 'border-primary-200 bg-primary-25' : 'border-line bg-surface', 'hover:border-primary-300'),
        className,
      )}
    >
      {floating ? (
        <span className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2">{floating}</span>
      ) : null}
      <span
        aria-hidden="true"
        className={cn(
          'absolute right-4 top-4 grid h-4 w-4 place-items-center rounded-full [&_svg]:!h-2.5 [&_svg]:!w-2.5',
          isSelected ? 'bg-primary-600 text-ink-on' : 'border border-line-strong bg-surface',
        )}
      >
        {isSelected ? <IconCheck /> : null}
      </span>
      <FeaturedIcon icon={icon} variant="light-circle" color={isSelected ? 'primary' : 'gray'} size="sm" />
      <span
        className={cn(
          'mt-5 text-base font-semibold',
          disabled ? 'text-ink-faint' : isSelected ? 'text-primary-800' : 'text-ink-secondary',
        )}
      >
        {title}
      </span>
      <span
        className={cn('mt-0.5 text-base', disabled ? 'text-ink-faint' : isSelected ? 'text-primary-700' : 'text-ink-muted')}
      >
        {description}
      </span>
      {disabled && disabledReason ? (
        <span data-disabled-reason="" className="mt-1 text-base text-ink-muted">
          {disabledReason}
        </span>
      ) : null}
    </button>
  )
}
